// Markdown renderer for user-authored profile text (company descriptions).
// `react-markdown` compiles the source to React elements — never through
// `dangerouslySetInnerHTML`, and raw HTML in the source is ignored by default —
// so arbitrary input cannot inject markup. The wrapper restyles the plain
// elements to sit naturally in the MUI dark theme.

import Box from '@mui/material/Box';
import ReactMarkdown from 'react-markdown';

interface MarkdownProps {
  children: string;
}

export function Markdown({ children }: MarkdownProps) {
  return (
    <Box
      sx={{
        color: 'text.primary',
        wordBreak: 'break-word',
        // Headings: scale down so profile text never shouts over the page chrome.
        '& h1': { typography: 'h5', mt: 2, mb: 1 },
        '& h2': { typography: 'h6', mt: 2, mb: 1 },
        '& h3, & h4, & h5, & h6': { typography: 'subtitle1', mt: 2, mb: 1 },
        '& h1:first-of-type, & h2:first-of-type, & h3:first-of-type': { mt: 0 },
        '& p': { typography: 'body1', my: 1 },
        '& p:first-of-type': { mt: 0 },
        '& p:last-of-type': { mb: 0 },
        '& ul, & ol': { my: 1, pl: 3 },
        '& li': { typography: 'body1', mb: 0.5 },
        '& a': { color: 'primary.main' },
        '& blockquote': {
          borderLeft: 3,
          borderColor: 'divider',
          color: 'text.secondary',
          pl: 2,
          ml: 0,
          my: 1,
        },
        '& code': {
          fontFamily: 'monospace',
          fontSize: '0.875em',
          backgroundColor: 'action.hover',
          borderRadius: 0.5,
          px: 0.5,
        },
        '& pre': {
          backgroundColor: 'action.hover',
          borderRadius: 1,
          p: 1.5,
          overflowX: 'auto',
        },
        '& pre code': { backgroundColor: 'transparent', px: 0 },
        '& hr': { border: 0, borderTop: 1, borderColor: 'divider', my: 2 },
        '& img': { maxWidth: '100%' },
      }}
    >
      <ReactMarkdown>{children}</ReactMarkdown>
    </Box>
  );
}
